import React, { useEffect, useMemo, useRef, useState } from 'react';

import { supabase } from '../../../utils/supabase.js';
import { closeCalibration } from '../../../utils/api';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/ui/AppIcon';
import {
  listBlocks,
  createBlock as apiCreateBlock,
  deleteBlock as apiDeleteBlock,
} from '../../../utils/api-blocks';
import {
  addMeasurement as apiAddMeasurement,
  listMeasurementsByBlock,
  deleteMeasurement as apiDeleteMeasurement,
  listMeasurementsByCalibration,
} from '../../../utils/api-measurements';
import CreateBlockModal from './CreateBlockModal';
import MeasurementForm from './MeasurementForm';
import SavedMeasurementsTable from './SavedMeasurementsTable';
import ErrorChart from './ErrorChart';
import { generatePdf, generateExcel } from '../../../utils/document-generator';

const BUCKET_CERTS = 'certificates';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const mean = (arr) => {
  const xs = arr.map(num).filter((v) => v !== null);
  if (!xs.length) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
};

const stdSample = (arr) => {
  const xs = arr.map(num).filter((v) => v !== null);
  const n = xs.length;
  if (n < 2) return null;
  const m = mean(xs);
  const s2 = xs.reduce((acc, x) => acc + (x - m) ** 2, 0) / (n - 1);
  return Math.sqrt(s2);
};

export default function MeasurementEditor({
  instrument,
  calibrationId,
  client,
  calibrationDetails,
  preferredBlockType = null,
  onCalibrationClosed,
}) {
  const [blocks, setBlocks] = useState([]);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [technicalName, setTechnicalName] = useState('');
  const autoBlockCreatedRef = useRef(false);

  const [setpoint, setSetpoint] = useState('');
  const [resolution, setResolution] = useState('');
  const [k, setK] = useState('2');
  const [refStd, setRefStd] = useState('');
  const [driftStd, setDriftStd] = useState('');
  const [otherStd, setOtherStd] = useState('');
  const [readings, setReadings] = useState(['', '', '']);
  const [readingsDecreasing, setReadingsDecreasing] = useState(['', '', '']);

  const activeBlock = useMemo(
    () => blocks.find((b) => b.id === activeBlockId) || null,
    [blocks, activeBlockId]
  );

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!calibrationId) return;

      try {
        const { data: cal, error: calErr } = await supabase
          .from('calibrations')
          .select('technician_name')
          .eq('id', calibrationId)
          .single();
        if (calErr) throw calErr;

        let nameFromDb = cal?.technician_name?.trim() || '';

        if (!nameFromDb) {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          nameFromDb = user?.user_metadata?.full_name || user?.email || '';
        }

        if (mounted) {
          setTechnicalName((prev) => prev || nameFromDb || '');
        }
      } catch (e) {
        console.warn('[tech-name preload]', e?.message || e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [calibrationId]);

  useEffect(() => {
    setSetpoint('');
    setResolution(instrument?.resolution ?? '');
    setK('2');
    setRefStd('');
    setDriftStd('');
    setOtherStd('');
    setReadings(['', '', '']);
    setReadingsDecreasing(['', '', '']);
  }, [activeBlockId, instrument]);

  useEffect(() => {
    if (!calibrationId) return;

    let cancelled = false;

    (async () => {
      try {
        const list = await listBlocks(calibrationId);
        if (cancelled) return;

        setBlocks(list);

        if (list.length > 0) {
          if (!activeBlockId) setActiveBlockId(list[0].id);
          return;
        }

        if (preferredBlockType && !autoBlockCreatedRef.current) {
          autoBlockCreatedRef.current = true;
          const created = await apiCreateBlock({
            calibration_id: calibrationId,
            block_type: preferredBlockType,
            mode: preferredBlockType === 'pressure_hysteresis' ? 'PRESSURE' : 'CUSTOM',
            unit: instrument?.unit || 'bar',
            display_name: preferredBlockType === 'pressure_hysteresis' ? 'Presostato' : 'Bloque automático',
          });

          const fresh = await listBlocks(calibrationId);
          if (cancelled) return;
          setBlocks(fresh);
          setActiveBlockId(created.id);
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [calibrationId, activeBlockId, instrument, preferredBlockType]);

  const addReading = () => setReadings((xs) => [...xs, '']);
  const removeReading = (i) => setReadings((xs) => xs.filter((_, idx) => idx !== i));
  const updateReading = (i, v) => setReadings((xs) => xs.map((val, idx) => (idx === i ? v : val)));

  const addReadingDecreasing = () => setReadingsDecreasing((xs) => [...xs, '']);
  const removeReadingDecreasing = (i) => setReadingsDecreasing((xs) => xs.filter((_, idx) => idx !== i));
  const updateReadingDecreasing = (i, v) =>
    setReadingsDecreasing((xs) => xs.map((val, idx) => (idx === i ? v : val)));

  const sr = useMemo(() => stdSample(readings), [readings]);

  const loadRows = async (blockId) => {
    const current = blockId || activeBlockId;
    if (!calibrationId || !current) {
      setRows([]);
      return;
    }

    setLoadingRows(true);
    try {
      const list = await listMeasurementsByBlock(current);
      setRows(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    if (activeBlockId) loadRows(activeBlockId).catch(console.error);
  }, [activeBlockId]);

  const handleSaveMeasurement = async () => {
    if (!activeBlockId || !setpoint) {
      alert('Por favor, completa el setpoint y selecciona un bloque.');
      return;
    }

    setSaving(true);
    try {
      const readingMean = mean(readings);
      if (readingMean === null) {
        alert('Ingresa al menos una lectura válida.');
        return;
      }

      const payload = {
        calibration_id: calibrationId,
        block_id: activeBlockId,
        setpoint: num(setpoint),
        reading: readingMean,
        unit: activeBlock?.unit || '',
        ref_std_unc: num(refStd),
        repeatability_std: stdSample(readings),
        resolution: num(resolution) ? num(resolution) / Math.sqrt(12) : null,
        drift_std: num(driftStd),
        other_std: num(otherStd),
        coverage_k: num(k),
      };

      if (activeBlock?.block_type === 'pressure_hysteresis') {
        payload.reading_decreasing = mean(readingsDecreasing);
      }

      await apiAddMeasurement(payload);
      setSetpoint('');
      setReadings(['', '', '']);
      setReadingsDecreasing(['', '', '']);
      await loadRows(activeBlockId);
    } catch (err) {
      alert(`Error al guardar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMeasurement = async (id) => {
    if (!confirm('¿Eliminar esta medición?')) return;
    try {
      await apiDeleteMeasurement(id);
      await loadRows(activeBlockId);
    } catch {
      alert('No se pudo eliminar la medición.');
    }
  };

  const handleDeleteBlock = async (id) => {
    if (!confirm('¿Eliminar este bloque y todas sus mediciones?')) return;
    try {
      await apiDeleteBlock(id);
      const list = await listBlocks(calibrationId);
      setBlocks(list);
      setActiveBlockId(list[0]?.id ?? null);
    } catch {
      alert('No se pudo eliminar el bloque.');
    }
  };

  const loadAllMeasurementsForCalibration = async () => {
    try {
      return await listMeasurementsByCalibration(calibrationId);
    } catch (e) {
      console.warn('[loadAllMeasurementsForCalibration]', e);
      alert('No se pudieron cargar todas las mediciones para el reporte.');
      return [];
    }
  };

  const handleExportExcel = async () => {
    const allRows = await loadAllMeasurementsForCalibration();
    if (!allRows.length) {
      alert('No hay mediciones para exportar.');
      return;
    }
    generateExcel({ client, instrument, calibrationDetails, blocks, measurements: allRows, technicalName });
  };

  const handleGeneratePDF = async () => {
    const allRows = await loadAllMeasurementsForCalibration();
    if (!allRows.length) {
      alert('No hay mediciones para generar el PDF.');
      return;
    }
    generatePdf({ client, instrument, calibrationDetails, blocks, measurements: allRows, technicalName });
  };

  async function handleGenerateAndUploadCertificate() {
    try {
      setUploadingCert(true);
      const allRows = await loadAllMeasurementsForCalibration();
      if (!allRows.length) {
        alert('No hay mediciones guardadas para generar el certificado.');
        return;
      }

      const fullData = { client, instrument, calibrationDetails, blocks, measurements: allRows, technicalName };
      const blob = generatePdf(fullData, { returnBlob: true });
      if (!blob) {
        alert('No se pudo generar el PDF.');
        return;
      }

      const fileName = `cert-${instrument?.tag || instrument?.id}-${calibrationId}.pdf`;
      const path = `${instrument?.id}/${calibrationId}/${crypto.randomUUID()}/${fileName}`;

      const { error: upErr } = await supabase.storage.from(BUCKET_CERTS).upload(path, blob, {
        contentType: 'application/pdf',
        upsert: false,
      });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from(BUCKET_CERTS).getPublicUrl(path);
      const publicUrl = pub?.publicUrl || null;

      const { error: updErr } = await supabase
        .from('calibrations')
        .update({ certificate_url: publicUrl, certificate_created_at: new Date().toISOString() })
        .eq('id', calibrationId);
      if (updErr) throw updErr;

      alert('Certificado generado y guardado.');
    } catch (e) {
      console.error('[cert-upload] error', e);
      alert('No se pudo generar/subir el certificado.');
    } finally {
      setUploadingCert(false);
    }
  }

  const handleCloseCalibration = async () => {
    if (!confirm('¿Cerrar calibración y actualizar fechas?')) return;
    setClosing(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const nameSnapshot =
        (technicalName && technicalName.trim()) ||
        user?.user_metadata?.full_name ||
        user?.email ||
        null;

      const { error: updErr } = await supabase
        .from('calibrations')
        .update({
          technician_name: nameSnapshot,
          technician_id: user?.id ?? null,
        })
        .eq('id', calibrationId);
      if (updErr) throw updErr;

      await closeCalibration({ calibrationId, instrumentId: instrument.id });

      onCalibrationClosed?.();
      alert('Calibración cerrada.');
    } catch (e) {
      console.error('[close] error', e);
      alert('No se pudo cerrar la calibración.');
    } finally {
      setClosing(false);
    }
  };

  const formState = {
    setpoint,
    resolution,
    k,
    refStd,
    driftStd,
    otherStd,
    readings,
    readingsDecreasing,
    repeatStdAuto: sr,
  };

  const formActions = {
    setSetpoint,
    setResolution,
    setK,
    setRefStd,
    setDriftStd,
    setOtherStd,
    addReading,
    removeReading,
    updateReading,
    addReadingDecreasing,
    removeReadingDecreasing,
    updateReadingDecreasing,
    setReadings,
    setReadingsDecreasing,
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div className="mb-6 max-w-lg">
        <label htmlFor="technicalName" className="block text-sm font-medium text-gray-700 mb-1">
          Técnico Responsable
        </label>
        <input
          type="text"
          id="technicalName"
          value={technicalName}
          onChange={(e) => setTechnicalName(e.target.value)}
          placeholder="Nombre Completo del Técnico"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Bloques de Prueba</h2>
          <Button variant="outline" onClick={() => setShowModal(true)}>
            <Icon name="Plus" size={14} className="mr-1" /> Agregar Bloque
          </Button>
        </div>
        {blocks.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {blocks.map((b) => (
              <button
                key={b.id}
                className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                  b.id === activeBlockId ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
                }`}
                onClick={() => setActiveBlockId(b.id)}
              >
                {b.display_name || b.mode}
              </button>
            ))}
          </div>
        )}
      </div>

      {activeBlock && (
        <div className="flex items-center justify-between rounded border p-3 bg-muted/50">
          <div className="text-sm">
            <div>
              <span className="text-muted-foreground">Prueba:</span> <strong>{activeBlock.display_name || activeBlock.mode}</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Tipo:</span> <code className="text-xs">{activeBlock.block_type}</code>
            </div>
          </div>
          <Button variant="destructive" size="sm" onClick={() => handleDeleteBlock(activeBlock.id)}>
            Eliminar
          </Button>
        </div>
      )}

      <div>
        <h2 className="text-xl font-semibold mb-4">Nueva Medición</h2>
        <MeasurementForm activeBlock={activeBlock} formState={formState} formActions={formActions} />
      </div>

      <div className="flex items-center justify-end">
        <Button onClick={handleSaveMeasurement} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar Medición'}
        </Button>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Mediciones Guardadas</h3>
        <SavedMeasurementsTable rows={rows} onDelete={handleDeleteMeasurement} />
      </div>

      <div className="border-t pt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Documentos y Finalización</h3>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={handleExportExcel} className="btn-outline-green" title="Exportar a Excel">
                ⬇️ Exportar a Excel
              </button>
              <button type="button" onClick={handleGeneratePDF} className="btn-outline-blue" title="Generar PDF">
                📄 Generar PDF
              </button>
              <button
                type="button"
                onClick={handleGenerateAndUploadCertificate}
                className="btn-outline-violet"
                title="Generar y subir certificado"
              >
                ☁️ Generar y subir certificado
              </button>
            </div>

            <Button onClick={handleCloseCalibration} disabled={closing || rows.length === 0}>
              {closing ? 'Cerrando…' : 'Cerrar Calibración'}
            </Button>
          </div>
        </div>
        {rows.length === 0 && (
          <p className="text-xs text-right text-muted-foreground mt-2">
            Guarda al menos una medición para generar documentos o cerrar la calibración.
          </p>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Gráfico de Errores</h3>
        <ErrorChart rows={rows} unit={activeBlock?.unit} />
      </div>

      <CreateBlockModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onCreate={async (payload) => {
          try {
            const created = await apiCreateBlock({ calibration_id: calibrationId, ...payload });
            const list = await listBlocks(calibrationId);
            setBlocks(list);
            setActiveBlockId(created.id);
            setShowModal(false);
          } catch (e) {
            alert(`No se pudo crear el bloque: ${e.message || e}`);
          }
        }}
      />
    </div>
  );
}
